/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { HttpResponse } from './types'

// Creates a standardized JSON Response object with the provided data
export const createJsonResponse = (data: any, status = 200): Response =>
  new Response(JSON.stringify(data), {
    status,
    headers: { 'Content-Type': 'application/json' },
  })

export const makeHttpRequest = async (
  path: string,
  options: any = {},
): Promise<{ response: Response; body: any }> => {
  let response: Response
  let body: any

  try {
    response = await fetch(path, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
      ...options,
    })
  } catch (err: any) {
    throw new Error(`${err.name ?? 'Error'}: ${err.message} (path: ${path})`)
  }

  const contentType = response.headers.get('Content-Type') || ''

  if (contentType.includes('application/json')) {
    body = await response.json()
  } else {
    body = await response.text()
  }

  return {
    response,
    body,
  }
}

export const normalizeDeploymentProxyResult = (result: any): any => {
  const payload = (result as any)?.data ?? (result as any)
  const { body: bodyresp } = payload ?? {}

  return bodyresp ?? payload
}

export const handleProxyError = (error: any): any =>
  error?.response?.body ??
  error?.body ??
  error?.data?.body ?? {
    error: error.message ?? 'Unknown error',
    status: error?.response?.status ?? error?.statusCode ?? error?.status ?? 500,
  }

/**
 * Checks if the request is an internal Kibana request
 * Internal Kibana requests have options.query.isKibanaRequest set to "true"
 */
export const isKibanaInternalRequest = (options?: any): boolean =>
  options?.query?.isKibanaRequest === 'true'

/**
 * Creates an error response for unsupported Kibana internal requests
 */
export const createKibanaRequestError = (): HttpResponse => {
  const errorStatusCode = 400
  const errorBody = {
    error: 'Internal kibana api calls are not yet supported here',
    status: errorStatusCode,
  }

  const response = createJsonResponse(errorBody, errorStatusCode)

  return {
    body: errorBody,
    response,
  }
}

/**
 * Creates an error response for not implemented HTTP methods
 */
export const createNotImplementedError = (method: string): HttpResponse => {
  const errorBody = {
    error: `${method} method is not yet implemented`,
    status: 501,
  }
  const response = createJsonResponse(errorBody, 501)

  return {
    body: errorBody,
    response,
  }
}
